import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	CORE_ROUTE,
	CoreUiInteractionEvent,
	JurisdictionPrefixService,
	ResponsibleGamingAction,
	ResponsibleGamingConfigService,
	TWELVE_HOUR_SELF_EXCLUSION,
	UiInteraction,
	UiInteractionAction,
} from "@obg/ngx.core";
import { CoreContentState, TranslationGroup } from "@obg/ngx.core.content";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import { TrackingContext } from "@obg/ngx.ui.core";
import { combineLatest, merge, Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { GamblingSummaryModalContainer } from "./gambling-summary/gambling-summary-modal.container";

const STANDARD_REGULATORY_BAR_AFTER_PANIC_BUTTON_CLICK_ALERT_ID =
	"STANDARD_REGULATORY_BAR_AFTER_PANIC_BUTTON_CLICK_ALERT_ID";
const STANDARD_REGULATORY_BAR_PANIC_BUTTON_CONFIRMATION_DIALOG_ALERT_ID =
	"STANDARD_REGULATORY_BAR_PANIC_BUTTON_CONFIRMATION_DIALOG_ALERT_ID";
const STANDARD_REGULATORY_BAR_OPEN_GAMBLING_SUMMERY_ALERT_ID = "STANDARD_REGULATORY_BAR_OPEN_GAMBLING_SUMMERY_ALERT_ID";

@Component({
	selector: "obg-standard-regulatory-bar",
	templateUrl: "./standard-regulatory-bar.container.html",
	styleUrls: ["./standard-regulatory-bar.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class StandardRegulatoryBarContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-standard-regulatory-bar") hostCssClass = true;
	@HostBinding("attr.test-id") testId = "sga-header";

	showSelfAssessmentButton = this.responsibleGamingConfigService.getHeaderSelfAssessmentButtonEnabled();
	showGameLimitsButton = this.responsibleGamingConfigService.getHeaderGameLimitsButtonEnabled();
	showPanicButton = this.responsibleGamingConfigService.getHeaderPanicButtonEnabled();
	isPanicButtonDisabled = this.responsibleGamingConfigService.getPanicButtonHoverIsEnabled();
	showGamblingSummary$ = this.responsibleGamingConfigService.getHeaderGamblingSummaryButtonEnabled$();
	sessionElapsedSeconds$ = this.store.select(AuthSelector.getSessionElapsedTimeInMillis);
	isAuthenticated = false;

	private data$$: Subscription | undefined;
	private panicButtonHoverTimeoutSubscription: number | undefined;
	private panicButtonHoverIsEnabled = this.responsibleGamingConfigService.getPanicButtonHoverIsEnabled();
	private panicButtonHoverDuration = this.responsibleGamingConfigService.getPanicButtonHoverDurationInMs();
	private panicButtonIsInstant = this.responsibleGamingConfigService.getPanicButtonIsInstant();
	private twelveHourSelfExclusion = this.responsibleGamingConfigService.getTwelveHourSelfExclusion();
	private panicButtonTimeout = this.responsibleGamingConfigService.getPanicButtonTimeout();
	private title = "";
	private message = "";

	constructor(
		private store: Store<CoreContentState>,
		private responsibleGamingConfigService: ResponsibleGamingConfigService,
		private uiInteractionAction: UiInteractionAction,
		private deviceConfigService: DeviceConfigService,
		private responsibleGamingAction: ResponsibleGamingAction,
		private changeDetector: ChangeDetectorRef,
		private alertService: AbstractTranslatableAlertService,
		private jurisdictionPrefixService: JurisdictionPrefixService
	) {}

	ngOnInit() {
		const isAuthenticated$ = this.store
			.select(AuthSelector.isAuthenticated)
			.pipe(tap((isAuthenticated) => (this.isAuthenticated = isAuthenticated)));

		const prefixes$ = combineLatest([
			this.jurisdictionPrefixService.addJurisdictionPrefix$("regulatory-bar.self-exclusion.action.title"),
			this.jurisdictionPrefixService.addJurisdictionPrefix$("regulatory-bar.self-exclusion.action.description"),
		]).pipe(
			tap(([title, message]) => {
				this.title = title;
				this.message = message;
			})
		);

		this.data$$ = merge(isAuthenticated$, prefixes$).subscribe();
	}

	ngOnDestroy() {
		if (this.data$$) {
			this.data$$.unsubscribe();
		}
		if (this.panicButtonHoverIsEnabled && this.panicButtonHoverTimeoutSubscription) {
			clearTimeout(this.panicButtonHoverTimeoutSubscription);
		}
	}

	openGamblingSummary(key: string) {
		this.store.dispatch(this.regulatoryBarInteractionEvent(key));
		this.alertService.open(
			{
				id: STANDARD_REGULATORY_BAR_OPEN_GAMBLING_SUMMERY_ALERT_ID,
				testId: STANDARD_REGULATORY_BAR_OPEN_GAMBLING_SUMMERY_ALERT_ID,
				title: "regulatory-bar.gambling-summary.title",
				messageComponent: GamblingSummaryModalContainer,
				buttons: [
					{
						text: "regulatory-bar.gambling-summary.action.transaction-history",
						handler: () => this.store.dispatch(RouteAction.navigate(CORE_ROUTE.transactionHistory.all)),
					},
					{
						text: "general.close",
					},
				],
			},
			[TranslationGroup.General]
		);
	}

	gameLimitsClick(key: string) {
		this.store.dispatch(
			batchActions(
				this.regulatoryBarInteractionEvent(key),
				RouteAction.navigate(CORE_ROUTE.responsibleGaming.root)
			)
		);
	}

	panicClick(key: string) {
		if (this.isPanicButtonDisabled) {
			return;
		}
		if (this.panicButtonIsInstant) {
			this.panicClickInstant();
			return;
		}
		this.store.dispatch(
			batchActions(
				this.regulatoryBarInteractionEvent(key),
				RouteAction.navigate(
					this.deviceConfigService.isDesktop()
						? CORE_ROUTE.responsibleGaming.timeOff
						: CORE_ROUTE.responsibleGaming.selfExclusion
				)
			)
		);
	}

	panicButtonMouseEnter() {
		if (!this.panicButtonHoverIsEnabled) {
			return;
		}
		this.panicButtonHoverTimeoutSubscription = window.setTimeout(() => {
			this.isPanicButtonDisabled = false;
			this.changeDetector.markForCheck();
		}, this.panicButtonHoverDuration);
	}

	panicButtonMouseLeave() {
		if (!this.panicButtonHoverIsEnabled) {
			return;
		}
		clearTimeout(this.panicButtonHoverTimeoutSubscription);
		this.isPanicButtonDisabled = true;
		this.changeDetector.markForCheck();
	}

	selfAssessmentClick(key: string) {
		this.store.dispatch(this.regulatoryBarInteractionEvent(key));
	}

	private panicClickInstant() {
		if (!this.panicButtonIsInstant || this.isPanicButtonDisabled || !this.isAuthenticated) {
			return;
		}

		if (this.twelveHourSelfExclusion) {
			this.showInstantPanicButtonConfirmationDialog();
			return;
		}

		this.store.dispatch(
			this.responsibleGamingAction.updateCoolOffPeriod({
				days: this.panicButtonTimeout.numberOfDays,
			})
		);

		if (this.responsibleGamingConfigService.showPanicButtonClickedMessage()) {
			this.afterPanicButtonClick();
		}
	}

	private regulatoryBarInteractionEvent(key: string): ActionWithPayload<UiInteraction> {
		return this.uiInteractionAction.emit({
			context: TrackingContext.RegulatoryBarInteractionContext,
			event: CoreUiInteractionEvent.Navigate,
			data: {
				ItemClicked: key,
			},
		});
	}

	private showInstantPanicButtonConfirmationDialog() {
		this.alertService.open(
			{
				id: STANDARD_REGULATORY_BAR_PANIC_BUTTON_CONFIRMATION_DIALOG_ALERT_ID,
				testId: STANDARD_REGULATORY_BAR_PANIC_BUTTON_CONFIRMATION_DIALOG_ALERT_ID,
				title: "regulatory-bar.self-exclusion.instant-self-exclusion-title",
				message: "regulatory-bar.self-exclusion.instant-self-exclusion-message",
				buttons: [
					{
						text: "general.close",
					},
					{
						text: "general.confirm",
						handler: () => {
							this.store.dispatch(
								this.responsibleGamingAction.updateInstantSelfExclusionPeriod(
									TWELVE_HOUR_SELF_EXCLUSION
								)
							);
							if (this.responsibleGamingConfigService.showPanicButtonClickedMessage()) {
								this.afterPanicButtonClick();
							}
						},
					},
				],
				mustBeInteracted: true,
			},
			[]
		);
	}

	private afterPanicButtonClick() {
		this.alertService.open(
			{
				hideClose: true,
				preserveOnLogout: true,
				dialogConfig: {
					closeOnNavigation: false,
					disableClose: true,
				},
				id: STANDARD_REGULATORY_BAR_AFTER_PANIC_BUTTON_CLICK_ALERT_ID,
				title: this.title,
				message: this.message,
				buttons: [
					{
						text: "general.ok",
					},
				],
			},
			[]
		);
	}
}
